<script setup lang="ts">
import { ref, computed, watch, watchEffect } from "vue";
import { useResizeObserver } from "@vueuse/core";
import type { Pull } from "../lib/types";
import { arenaFor, type View } from "../config/arenas";
import { activeMapAt, isDeadAt, sampleAt, mapProjection } from "../lib/replay";

const props = defineProps<{ pull: Pull; time: number }>();

const canvas = ref<HTMLCanvasElement | null>(null);

// The map active at the current scrub time. Each WoW UiMap is its own coordinate
// space, so the replay only draws units on the active map.
const activeMap = computed(() => activeMapAt(props.pull.maps, props.time));

// "3/7" — which map window is active, out of the pull's total.
const mapPosition = computed(() => {
  const i = props.pull.maps.indexOf(activeMap.value!);
  return `${i + 1}/${props.pull.maps.length}`;
});

// Authored config (world bounds + the initial camera `view`) for the active map.
const arena = computed(() =>
  activeMap.value ? arenaFor(props.pull.meta.id, activeMap.value.ui_map_id) : undefined,
);

// Live camera over the map. Seeded from the authored `view` (the window framed
// in the editor); the viewer can then pan (drag), zoom (wheel) and rotate from
// there. Reset to the authored view whenever the active map changes.
const camera = ref<View | null>(null);
watch(arena, (a) => { camera.value = a ? { ...a.view } : null; }, { immediate: true });
const cameraMoved = computed(() => {
  const c = camera.value, v = arena.value?.view;
  return !!c && !!v &&
    (c.centerX !== v.centerX || c.centerY !== v.centerY ||
     c.scale !== v.scale || c.rotation !== v.rotation);
});

// Zone map art, one PNG per UiMapID (see tools/fetch-maps.mjs + the wow-maps
// skill). Lazily loaded and cached; the canvas falls back to a flat fill until
// the image decodes.
const mapArt = new Map<number, HTMLImageElement>();
function artFor(id: number): HTMLImageElement | null {
  let img = mapArt.get(id);
  if (!img) {
    img = new Image();
    img.onload = () => draw(); // redraw once the art decodes (we don't poll)
    img.src = `/maps/${id}.png`;
    mapArt.set(id, img);
  }
  return img.complete && img.naturalWidth ? img : null;
}

const COLOR = { player: "#d4b15a", npc: "#e2705a", pet: "#9aa3b2" } as const;

function draw() {
  const cv = canvas.value;
  if (!cv) return;
  const ctx = cv.getContext("2d")!;
  const dpr = window.devicePixelRatio || 1;
  const w = cv.clientWidth, h = cv.clientHeight;
  if (cv.width !== w * dpr || cv.height !== h * dpr) {
    cv.width = w * dpr;
    cv.height = h * dpr;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  const map = activeMap.value;
  if (!map) return;

  // Bounds + view are AUTHORED per (encounter, map) pair, never guessed from
  // positions — a guessed rect is wrong whenever the raid doesn't cover the
  // whole arena. An unconfigured pair renders a placeholder, not fake positions.
  const cfg = arena.value;
  if (!cfg || cfg.bounds.north <= cfg.bounds.south || cfg.bounds.west <= cfg.bounds.east) {
    ctx.fillStyle = "#0c0e13";
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = "#52525b";
    ctx.font = "13px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`Map ${map.ui_map_id} not configured`, w / 2, h / 2);
    return;
  }

  // The art PNG must be loaded to know its pixel dims (the crop/rotation are
  // expressed against it). Until it decodes, show the flat fill.
  const art = artFor(map.ui_map_id);
  if (!art) {
    ctx.fillStyle = "#0c0e13";
    ctx.fillRect(0, 0, w, h);
    return;
  }

  // One projection drives both the art and the unit dots, built from the LIVE
  // camera (seeded from the authored view, then user-pannable). The same affine
  // is applied to world positions, so dots can never desync from the art.
  const proj = mapProjection(
    { bounds: cfg.bounds, view: camera.value ?? cfg.view },
    art.naturalWidth, art.naturalHeight, w, h,
  );

  // Draw the art under the projection's matrix (composed with the DPR transform).
  ctx.setTransform(new DOMMatrix().scaleSelf(dpr, dpr).multiplySelf(proj.imageToCanvas));
  ctx.drawImage(art, 0, 0);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // back to pixel space for the dots

  const t = props.time;

  // Players persist the whole fight, so we always show them (clamped to their
  // last known spot). NPCs/pets exist only briefly — only draw them while the
  // scrub time is within their active window, or summons from a later phase
  // would all pile onto the screen frozen at their first position.
  const GRACE = 3000;

  for (const a of props.pull.actors) {
    if (a.kind !== "player") {
      const first = a.track[0].t, lastT = a.track[a.track.length - 1].t;
      if (t < first - GRACE || t > lastT + GRACE) continue;
    }

    const s = sampleAt(a.track, t);
    if (!s || s.map !== map.ui_map_id) continue; // only units on the active map

    const [x, y] = proj.project(s.x, s.y);
    const dead = isDeadAt(a, t);
    const r = a.kind === "player" ? 6 : a.kind === "npc" ? 5 : 3;

    if (dead) {
      ctx.strokeStyle = "#6b7280";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x - 4, y - 4); ctx.lineTo(x + 4, y + 4);
      ctx.moveTo(x + 4, y - 4); ctx.lineTo(x - 4, y + 4);
      ctx.stroke();
      continue;
    }

    ctx.fillStyle = COLOR[a.kind];
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();

    if (a.kind === "player") {
      ctx.strokeStyle = s.hp > 0.5 ? "#6fc28b" : s.hp > 0.25 ? "#e1b562" : "#e2705a";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, r + 2.5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * s.hp);
      ctx.stroke();
    }
  }
}

// --- live camera: zoom IN and pan, confined to the authored crop -------------
// The authored view is the most-zoomed-out state: you can zoom in (up to
// MAX_ZOOM_IN×) and pan, but the viewport can never show anything outside the
// crop the editor framed. At the base zoom there's no slack, so it's pinned.
// Rotation is fixed to the crop's — a differently-rotated viewport can't be
// confined inside the crop rectangle, so there's no runtime rotate control.
const MAX_ZOOM_IN = 4;

// Canvas-pixel coords of a pointer event (CSS px; the matrix works in CSS px,
// the DPR scale is applied separately in draw()).
function canvasXY(e: PointerEvent | WheelEvent): [number, number] {
  const cv = canvas.value!;
  const r = cv.getBoundingClientRect();
  return [
    ((e.clientX - r.left) / r.width) * cv.clientWidth,
    ((e.clientY - r.top) / r.height) * cv.clientHeight,
  ];
}

// Clamp the live camera so it never zooms out past the crop, nor pans to reveal
// anything outside it. Works in the crop's rotated frame, where both the crop
// and the viewport are axis-aligned rects: the viewport half-extents shrink as
// you zoom in, and the centre is free to move within (cropHalf − viewportHalf).
function clampCamera() {
  const cv = canvas.value, base = arena.value?.view, cam = camera.value;
  const art = activeMap.value && mapArt.get(activeMap.value.ui_map_id);
  if (!cv || !base || !cam || !art?.naturalWidth) return;
  const iw = art.naturalWidth, ih = art.naturalHeight, cw = cv.clientWidth, ch = cv.clientHeight;

  // Never below the authored scale (no zoom-out past the crop); allow zoom-in.
  cam.scale = Math.min(base.scale * MAX_ZOOM_IN, Math.max(base.scale, cam.scale));

  // Work in the crop's rotated frame, where crop and viewport are both axis-aligned.
  const rad = (base.rotation * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const toFrame = (x: number, y: number) => [x * cos + y * sin, -x * sin + y * cos];
  const fromFrame = (u: number, v: number) => [u * cos - v * sin, u * sin + v * cos];

  // Pan slack = how far the centre can move before the (smaller, zoomed-in)
  // viewport's edge reaches the crop's edge. Zero at base scale → pinned.
  const hx = iw / (2 * cam.scale), hy = (iw * ch) / (2 * cw * cam.scale);
  const slackX = Math.max(0, iw / (2 * base.scale) - hx);
  const slackY = Math.max(0, (iw * ch) / (2 * cw * base.scale) - hy);

  // Clamp the camera centre within ±slack of the authored centre, in that frame.
  const [au, av] = toFrame(base.centerX * iw, base.centerY * ih);
  let [u, v] = toFrame(cam.centerX * iw, cam.centerY * ih);
  u = Math.min(au + slackX, Math.max(au - slackX, u));
  v = Math.min(av + slackY, Math.max(av - slackY, v));

  const [px, py] = fromFrame(u, v);
  cam.centerX = px / iw;
  cam.centerY = py / ih;
}
// Image-pixel point under a canvas point, via the current camera matrix.
function imageAt(cx: number, cy: number): [number, number] | null {
  const cv = canvas.value, map = activeMap.value, cfg = arena.value, cam = camera.value;
  if (!cv || !map || !cfg || !cam) return null;
  const art = mapArt.get(map.ui_map_id);
  if (!art?.naturalWidth) return null;
  const proj = mapProjection(
    { bounds: cfg.bounds, view: cam }, art.naturalWidth, art.naturalHeight,
    cv.clientWidth, cv.clientHeight,
  );
  const p = proj.imageToCanvas.inverse().transformPoint({ x: cx, y: cy });
  return [p.x, p.y];
}

let dragging = false;
let anchor: [number, number] | null = null;
function onPointerDown(e: PointerEvent) {
  anchor = imageAt(...canvasXY(e));
  if (!anchor) return;
  dragging = true;
  canvas.value!.setPointerCapture(e.pointerId);
}
function onPointerMove(e: PointerEvent) {
  if (!dragging || !camera.value || !anchor) return;
  const art = mapArt.get(activeMap.value!.ui_map_id)!;
  const here = imageAt(...canvasXY(e));
  if (!here) return;
  // Keep the grabbed image-point glued under the cursor (centre is in fractions).
  camera.value.centerX -= (here[0] - anchor[0]) / art.naturalWidth;
  camera.value.centerY -= (here[1] - anchor[1]) / art.naturalHeight;
  clampCamera();
  draw();
}
function onPointerUp(e: PointerEvent) {
  dragging = false;
  canvas.value?.releasePointerCapture(e.pointerId);
}
function onWheel(e: WheelEvent) {
  if (!camera.value) return;
  const [cx, cy] = canvasXY(e);
  const before = imageAt(cx, cy);
  if (!before) return;
  camera.value.scale *= e.deltaY < 0 ? 1.1 : 1 / 1.1;
  clampCamera(); // enforce zoom-in-only bounds before re-anchoring
  const after = imageAt(cx, cy); // re-anchor so the point under the cursor stays put
  if (!after) return;
  const art = mapArt.get(activeMap.value!.ui_map_id)!;
  camera.value.centerX += (before[0] - after[0]) / art.naturalWidth;
  camera.value.centerY += (before[1] - after[1]) / art.naturalHeight;
  clampCamera(); // confine pan to the crop
  draw();
}
function resetCamera() {
  if (!arena.value) return;
  camera.value = { ...arena.value.view };
  draw();
}

// Redraw only when something actually changes. draw() reads props.time,
// props.pull and activeMap.value reactively, so watchEffect tracks exactly
// those deps (no idle rAF loop, no hand-listed dep array). useResizeObserver
// fires once on mount too, so it covers the initial draw and manages its own
// teardown (no manual disconnect); it ignores the entry arg, so draw passes
// directly. The map art finishing decode redraws via artFor's onload.
watchEffect(draw);
useResizeObserver(canvas, () => { clampCamera(); draw(); });
</script>

<template>
  <div class="flex h-full w-full min-h-0 items-center justify-center [container-type:size]">
    <div class="relative aspect-video w-[min(100cqw,177.778cqh)] max-w-full max-h-full">
    <canvas
      ref="canvas"
      class="h-full w-full cursor-grab touch-none rounded border border-zinc-800 active:cursor-grabbing"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @wheel.prevent="onWheel"
    ></canvas>
    <div
      v-if="activeMap"
      class="absolute left-2 top-2 rounded bg-zinc-900/70 px-2 py-1 text-xs text-zinc-300"
    >
      Map {{ activeMap.ui_map_id }}
      <span class="text-zinc-500">· {{ mapPosition }}</span>
    </div>
    <div v-if="cameraMoved" class="absolute right-2 top-2 flex items-center gap-2 text-zinc-300">
      <button
        type="button"
        class="rounded bg-zinc-900/70 px-2 py-1 text-xs transition-colors hover:bg-zinc-100/10 hover:text-zinc-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-400"
        @click="resetCamera"
      >Reset</button>
    </div>
    </div>
  </div>
</template>
